import type { InvoiceLineItem } from 'payments-api/model';
import type { InvoiceStatus } from 'payments-api/model/invoiceStatus';
import {
  useGetInvoicesIdSuspense,
  useGetProductsSuspense,
  useGetUsersSuspense,
  usePatchInvoicesId,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { useToast } from 'components/ToastManager';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useFlagEnabledWithOverrides } from 'hooks/useFlagEnabledWithOverrides';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';

import lilId from '@fragment/lil-id';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { ItemTableItem } from './ItemTable';
import { darkenHex, getStableUserColorHex } from './constants';
import { FormatBillingAmount } from './formatBillingAmount';
import { InvoiceDetailLoading } from './InvoiceDetailLoading';
import { getTotalsFromItems, InvoiceItemsSection } from './InvoiceItemsSection';
import { InvoiceLayout } from './InvoiceLayout';
import { PaymentConfirmationModal } from './PaymentConfirmationModal';
import { ProductDetailModal } from './ProductDetailModal';
import { ReconcileModal } from './ReconcileModal';
import { UserDetailModal } from './UserDetailModal';

type Props = {
  workspaceId: string;
  invoiceId: string;
};

const lineItemsToItemTableItems = (
  lineItems: InvoiceLineItem[] | undefined
): ItemTableItem[] => {
  if (!lineItems) return [];
  return lineItems.map((li) => ({
    id: li.id,
    type: li.type,
    product_id: li.product_id,
    amount: li.price.amount,
    currencyCode: li.currency_code,
    description: li.description,
    user_id: li.user_id,
    quantity: li.price.quantity,
    unitPrice: li.price.unit_price,
  }));
};

const InvoiceDetailPageContent = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { data } = useGetInvoicesIdSuspense(invoiceId);
  const { data: productsData } = useGetProductsSuspense();
  const { data: usersData } = useGetUsersSuspense();
  const patchInvoicesIdMutation = usePatchInvoicesId();
  const [, setLocation] = useLocation();
  const [isAdjusting, setIsAdjusting] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const isPaymentsForReal = useFlagEnabledWithOverrides(
    'dashboard-payments-for-real'
  );

  const buildNonAdjustingTertiaryAction = () => {
    if (isPaymentsForReal) {
      return {
        title: 'Pay',
        'data-testid': 'pay-invoice-button',
        onClick: () => setShowPayModal(true),
      };
    }
    return {
      title: 'Reconcile',
      'data-testid': 'reconcile-invoice-button',
      onClick: () => setShowReconcile(true),
    };
  };
  const [invoiceItems, setInvoiceItems] = useState<ItemTableItem[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<ItemTableItem | null>(null);
  const usersDataList = usersData.data;
  const products = productsData.data;

  const invoice = data.data;

  useDocumentTitle(`Invoice ${invoice.id}`);

  const iconColors = useMemo(() => {
    const invoiceUserIds = new Set(
      (invoice.line_items ?? []).map((li) => li.user_id)
    );
    const colors = Array.from(invoiceUserIds).map((uid) =>
      getStableUserColorHex(uid)
    );
    if (colors.length === 1) {
      colors.push(darkenHex(colors[0]));
    }
    return colors;
  }, [invoice.line_items]);

  const displayItems = isAdjusting
    ? invoiceItems
    : lineItemsToItemTableItems(invoice.line_items);

  const handleBackToInvoices = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
    );
  }, [workspaceId, setLocation]);

  const handleStartAdjust = useCallback(() => {
    setInvoiceItems(lineItemsToItemTableItems(invoice.line_items));
    setIsAdjusting(true);
  }, [invoice.line_items]);

  const handleAddInvoiceItem = useCallback(
    (item: Omit<ItemTableItem, 'id'>) => {
      setInvoiceItems((prev) => [...prev, { ...item, id: lilId() }]);
    },
    []
  );

  const handleUpdateInvoiceItem = useCallback(
    (id: string, item: Omit<ItemTableItem, 'id'>) => {
      setInvoiceItems((prev) =>
        prev.map((i) => (i.id === id ? { ...item, id } : i))
      );
    },
    []
  );

  const removeItem = useCallback((id: string) => {
    setInvoiceItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleSubmitChanges = useCallback(async () => {
    if (!invoiceId || !invoice.line_items) return;
    const serverIds = new Set(invoice.line_items.map((li) => li.id));
    const serverMap = new Map(invoice.line_items.map((li) => [li.id, li]));

    const deleteOps = invoice.line_items
      .filter((li) => !invoiceItems.some((i) => i.id === li.id))
      .map((li) => ({ id: li.id }));

    const updateOps = invoiceItems
      .filter((i) => serverIds.has(i.id))
      .filter((i) => {
        const server = serverMap.get(i.id);
        if (!server) return false;
        const qty = i.quantity ?? 1;
        const unitPrice = i.unitPrice ?? i.amount;
        return (
          String(server.price.amount) !== String(i.amount) ||
          String(server.price.unit_price) !== String(unitPrice) ||
          server.price.quantity !== qty
        );
      })
      .map((i) => {
        const quantity = i.quantity ?? 1;
        const unitPrice = i.unitPrice ?? i.amount;
        return {
          id: i.id,
          price: {
            amount: i.amount,
            unit_price: unitPrice,
            quantity,
          },
        };
      });

    const createOps = invoiceItems
      .filter((i) => !serverIds.has(i.id))
      .map((i) => ({
        type: i.type,
        product_id: i.product_id,
        price: { amount: i.amount },
        currency_code: i.currencyCode,
        description: i.description,
        user: { external_id: i.user_id },
      }));

    const hasChanges =
      deleteOps.length > 0 || updateOps.length > 0 || createOps.length > 0;
    if (!hasChanges) return;

    try {
      const response = await patchInvoicesIdMutation.mutateAsync({
        id: invoiceId,
        data: {
          current_invoice_version: Math.max(invoice.version, 1),
          line_items: {
            ...(deleteOps.length > 0 && { delete: deleteOps }),
            ...(updateOps.length > 0 && { update: updateOps }),
            ...(createOps.length > 0 && { create: createOps }),
          },
        },
      });
      // Update cache directly to avoid Suspense refetch flash.
      // Merge with existing data to preserve fields the mutation doesn't return (e.g. users/subtotals).
      queryClient.setQueryData([`/invoices/${invoiceId}`], (old: unknown) => {
        const prev = old as Record<string, unknown> | undefined;
        const resp = response as unknown as Record<string, unknown>;
        if (!prev) return response;
        const prevData = prev.data as Record<string, unknown> | undefined;
        const respData = resp.data as Record<string, unknown> | undefined;
        return {
          ...prev,
          ...resp,
          data: { ...prevData, ...respData },
        };
      });
      // Invalidate in background without awaiting
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- grandfathered
      queryClient.invalidateQueries({ queryKey: ['/invoices'] });
      setIsAdjusting(false);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save changes.';
      showToast(<Toast type="error" message={message} />);
    }
  }, [
    invoiceId,
    invoice,
    invoiceItems,
    patchInvoicesIdMutation,
    queryClient,
    showToast,
  ]);

  const handleCancelAdjust = useCallback(() => {
    setIsAdjusting(false);
  }, []);

  const hasChanges = useMemo(() => {
    if (!invoice.line_items || !isAdjusting) return false;
    const serverIds = new Set(invoice.line_items.map((li) => li.id));
    const serverMap = new Map(invoice.line_items.map((li) => [li.id, li]));
    const currentIds = new Set(invoiceItems.map((i) => i.id));
    if (invoice.line_items.some((li) => !currentIds.has(li.id))) return true;
    if (invoiceItems.some((i) => !serverIds.has(i.id))) return true;
    return invoiceItems.some((i) => {
      const server = serverMap.get(i.id);
      if (!server) return false;
      const qty = i.quantity ?? 1;
      const unitPrice = i.unitPrice ?? i.amount;
      return (
        String(server.price.amount) !== String(i.amount) ||
        String(server.price.unit_price) !== String(unitPrice) ||
        server.price.quantity !== qty
      );
    });
  }, [invoice.line_items, isAdjusting, invoiceItems]);

  const canSubmitAdjustment = hasChanges && !patchInvoicesIdMutation.isPending;

  const totalPayinContent = useMemo(() => {
    const { totalAmount } = getTotalsFromItems(displayItems);
    if (totalAmount.length === 0) return null;
    return totalAmount.map(({ amount, currency }) => (
      <FormatBillingAmount
        key={currency}
        amount={amount}
        currencyCode={currency}
      />
    ));
  }, [displayItems]);

  return (
    <>
      <InvoiceLayout
        onBack={handleBackToInvoices}
        title={invoice.id}
        status={invoice.status as InvoiceStatus}
        primaryAction={
          isAdjusting
            ? {
                title: patchInvoicesIdMutation.isPending
                  ? 'Submitting...'
                  : 'Submit',
                'data-testid': 'submit-invoice-button',
                // eslint-disable-next-line @typescript-eslint/no-misused-promises -- grandfathered
                onClick: handleSubmitChanges,
                disabled: !canSubmitAdjustment,
              }
            : undefined
        }
        secondaryAction={
          isAdjusting
            ? {
                title: 'Cancel',
                'data-testid': 'cancel-invoice-button',
                onClick: handleCancelAdjust,
              }
            : {
                title: 'Adjust',
                'data-testid': 'adjust-invoice-button',
                onClick: handleStartAdjust,
              }
        }
        tertiaryAction={
          isAdjusting ? undefined : buildNonAdjustingTertiaryAction()
        }
        issued={invoice.created}
        totalPayinContent={totalPayinContent}
        iconColors={iconColors}
      >
        <InvoiceItemsSection
          invoiceId={invoiceId}
          items={displayItems}
          products={products}
          users={usersDataList}
          onAddItem={isAdjusting ? handleAddInvoiceItem : undefined}
          onUpdateItem={isAdjusting ? handleUpdateInvoiceItem : undefined}
          onRemoveItem={isAdjusting ? removeItem : undefined}
          onUserClick={setSelectedUserId}
          onItemClick={setSelectedItem}
          showUnbalancedWarning={isAdjusting}
          payments={invoice.payments}
          userBalances={invoice.users}
          defaultTab={isAdjusting ? 'items' : 'summary'}
        />
      </InvoiceLayout>
      {selectedUserId &&
        (() => {
          const user = usersDataList.find(
            (u) => u.external_id === selectedUserId
          );
          if (!user) return null;
          const userLineItems = (invoice.line_items ?? [])
            .filter((li) => li.user_id === selectedUserId)
            .map((li) => ({
              type: li.type,
              amount: li.amount,
              currencyCode: li.currency_code,
            }));
          return (
            <UserDetailModal
              user={user}
              invoiceLineItems={userLineItems}
              workspaceId={workspaceId}
              onClose={() => setSelectedUserId(null)}
            />
          );
        })()}
      {selectedItem &&
        (() => {
          const product = products.find(
            (p) => p.code === selectedItem.product_id
          );
          if (!product) return null;
          return (
            <ProductDetailModal
              product={product}
              lineItem={selectedItem}
              onClose={() => setSelectedItem(null)}
            />
          );
        })()}
      {showReconcile && (
        <ReconcileModal
          invoiceId={invoiceId}
          items={displayItems}
          users={usersDataList}
          onClose={() => {
            setShowReconcile(false);
            // Ensure the PaymentsTable refetches after reconciliation
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- grandfathered
            queryClient.invalidateQueries({ queryKey: ['/transactions'] });
          }}
        />
      )}
      {showPayModal && (
        <PaymentConfirmationModal
          invoiceId={invoiceId}
          users={usersDataList}
          userBalances={invoice.users}
          items={displayItems}
          payments={invoice.payments}
          onClose={() => setShowPayModal(false)}
        />
      )}
    </>
  );
};

export const InvoiceDetailPage = ({
  workspaceId,
  invoiceId,
}: Props): JSX.Element => (
  <Suspense fallback={<InvoiceDetailLoading />}>
    <InvoiceDetailPageContent workspaceId={workspaceId} invoiceId={invoiceId} />
  </Suspense>
);
